import LndButton from '@components/LndButton';
import TextArea from 'antd/es/input/TextArea';
import { Modal, Typography } from 'antd';
import type React from 'react';
import { useState } from 'react';
import FeedbackService from '@apis/FeedbackService';
import { toast } from 'react-toastify';

interface FeedBackFormProps {
  open: boolean;
  onCancel: () => void;
}

const FeedBackForm: React.FC<FeedBackFormProps> = ({ open, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [selectedRating, setSelectedRating] = useState<number>(0);
  const [feedback, setFeedback] = useState('');
  
  const ratingEmojis = [
    { rating: 1, emoji: '😞', label: 'Very Unlikely', color: '#be123c' },
    { rating: 2, emoji: '😕', label: 'Unlikely', color: '#dc2626' },
    { rating: 3, emoji: '😐', label: 'Neutral', color: '#6b7280' },
    { rating: 4, emoji: '😊', label: 'Likely', color: '#059669' },
    { rating: 5, emoji: '🤩', label: 'Very Likely', color: '#ea580c' },
  ];

  const handleRatingClick = (rating: number) => {
    setSelectedRating(rating);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedRating === 0) {
      toast.error('Please select a rating');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ratings: selectedRating,
        user_feedback: feedback,
      };

      await FeedbackService.submitFeedback(payload);
      onCancel();
      toast.success('Thank you for your feedback!');
      setFeedback('');
      setSelectedRating(0);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      toast.error(`Failed to submit feedback: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    FeedbackService.dismissFeedback();
    setFeedback('');
    setSelectedRating(0);
    onCancel();
  };

  if (!open) return null;

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={500}
      centered
    >
      <div className="bg-card rounded-2xl  w-full mx-auto ">
        <div className="p-8">
          {/* Header Section */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">💭</span>
            </div>
            <h2 className="text-2xl font-bold text-card-foreground mb-2">
              Share Your Feedback
            </h2>
            <p className="text-muted-foreground">
              Help us improve by sharing your experience
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Rating Section */}
            <div className="text-center">
              <Typography.Title
                level={4}
                className="block text-lg font-semibold text-card-foreground mb-6"
              >
                How likely would you be to recommend this to a friend?
              </Typography.Title>
              <div className="flex justify-center gap-4 mb-2">
                {ratingEmojis.map(({ rating, emoji, label }) => (
                  <button
                    key={rating}
                    type="button"
                    onClick={() => handleRatingClick(rating)}
                    className={`
                      group relative flex flex-col items-center p-4 rounded-xl transition-all duration-200 
                      hover:scale-110 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary
                      ${selectedRating === rating ? 'bg-primary/10 shadow-md transform scale-105' : 'hover:bg-muted/50'}
                    `}
                    style={{
                      minWidth: '80px',
                    }}
                  >
                    <span
                      className="text-4xl mb-2 transition-transform duration-200 group-hover:scale-110"
                      style={{
                        filter:
                          selectedRating === rating
                            ? 'brightness(1.2)'
                            : 'brightness(1)',
                      }}
                    >
                      {emoji}
                    </span>
                    <span
                      className={`
                        text-xs font-medium text-center leading-tight transition-colors duration-200
                        ${selectedRating === rating ? 'text-primary font-semibold' : 'text-muted-foreground'}
                      `}
                    >
                      {label}
                    </span>
                    {selectedRating === rating && (
                      <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-8 h-1 rounded-full bg-primary" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Feedback Text Input */}
            <div className="space-y-2">
              <Typography.Text className="text-base font-medium text-card-foreground">
                Tell us more
              </Typography.Text>
              <TextArea
                id="feedback"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Share your thoughts, suggestions, or any issues you encountered..."
                maxLength={500}
                rows={4}
                className="resize-none"
              />
              <div className="text-right text-sm text-muted-foreground">
                {feedback.length}/500
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex gap-3 pt-4">
              <LndButton
                onClick={handleCancel}
                className="flex-1 bg-transparent"
              >
                Cancel
              </LndButton>
              <LndButton
                type="primary"
                disabled={
                  selectedRating === 0 || feedback.length === 0 || loading
                }
                className="flex-1"
                onClick={handleSubmit}
              >
                {loading ? 'Submitting...' : 'Submit Feedback'}
              </LndButton>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export default FeedBackForm;
